import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Flame, ShieldCheck, Truck, MessageCircleQuestion, Volume2, VolumeX } from "lucide-react";
import { api, scrollToId, usePageMeta } from "@/lib/api";
import { HERO_IMG, PROBLEMS, GUIDES } from "@/lib/data";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";
import { CookieBanner } from "@/components/CookieBanner";
import { Reveal, MaskedLine } from "@/components/Reveal";

const MARQUEE_ITEMS = [
  "Organiza tu hogar", "Ahorra tiempo cada día", "Cocina sin caos", "Cables bajo control",
  "Viaja ligero", "Coche ordenado", "Recomendaciones con transparencia", "Pequeños cambios, grandes resultados",
];

const HERO_VIDEO = "/hero-video.mp4";

const Hero = () => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [soundOn, setSoundOn] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  useEffect(() => {
    videoRef.current?.play().catch(() => {});
  }, []);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.play().catch(() => {});
    setSoundOn(!v.muted);
  };

  return (
    <section ref={ref} className="relative overflow-hidden bg-slate-950 hero-grain" data-testid="hero-section">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <video
          ref={videoRef}
          src={HERO_VIDEO}
          poster={HERO_IMG}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          data-testid="hero-video"
          className="w-full h-full object-cover opacity-65"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/75 to-slate-950/25" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36">
        <MaskedLine delay={0.1} className="mb-1">
          <span className="text-amber-400 font-mono-s360 text-xs sm:text-sm uppercase tracking-[0.25em]">
            Soluciones inteligentes para cada día
          </span>
        </MaskedLine>
        <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-6xl leading-[1.08] max-w-3xl">
          <MaskedLine delay={0.25}>Pequeñas soluciones.</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-amber-400">Grandes cambios</span> en tu día a día
          </MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="text-slate-300 text-base sm:text-lg mt-6 max-w-xl leading-relaxed"
        >
          Descubre productos prácticos que te ayudan a ahorrar tiempo, organizar tu hogar y
          simplificar tu rutina.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="flex flex-wrap gap-3 mt-8"
        >
          <button
            onClick={() => scrollToId("categorias")}
            data-testid="hero-discover-button"
            className="bg-amber-500 hover:bg-amber-400 active:scale-[0.98] text-slate-950 font-bold px-6 py-3.5 rounded-lg flex items-center gap-2 transition-[background-color,transform] shadow-lg shadow-amber-500/20"
          >
            Descubrir productos <ArrowRight size={17} strokeWidth={2.5} />
          </button>
          <button
            onClick={() => scrollToId("favoritos")}
            data-testid="hero-popular-button"
            className="border border-slate-500 hover:border-amber-400 hover:text-amber-400 text-white font-semibold px-6 py-3.5 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Flame size={17} /> Ver los más populares
          </button>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="text-slate-500 text-xs mt-8 flex items-center gap-2"
        >
          <ShieldCheck size={14} className="text-amber-400" />
          Recomendaciones independientes · La compra se completa en Amazon.es
        </motion.p>
      </div>

      <button
        onClick={toggleSound}
        data-testid="hero-sound-toggle"
        aria-label={soundOn ? "Quitar el sonido del vídeo" : "Activar el sonido del vídeo"}
        className="absolute bottom-5 right-5 z-10 flex items-center gap-2 bg-slate-950/70 hover:bg-slate-900 border border-slate-600 hover:border-amber-400 text-white text-xs font-semibold px-4 py-2.5 rounded-full backdrop-blur-sm transition-colors"
      >
        {soundOn ? <Volume2 size={15} className="text-amber-400" /> : <VolumeX size={15} />}
        {soundOn ? "Sonido activado" : "Activar sonido"}
      </button>
    </section>
  );
};

const Marquee = () => (
  <div className="bg-amber-400 text-slate-950 overflow-hidden py-2.5 border-y border-amber-500" data-testid="editorial-marquee">
    <div className="flex whitespace-nowrap animate-marquee w-max">
      {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
        <span key={i} className="font-display font-semibold text-sm px-6 flex items-center gap-6">
          {item} <span className="text-slate-700">·</span>
        </span>
      ))}
    </div>
  </div>
);

const SectionTitle = ({ eyebrow, title, text }) => (
  <Reveal>
    <p className="text-amber-600 font-mono-s360 text-xs uppercase tracking-[0.22em] mb-2">{eyebrow}</p>
    <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{title}</h2>
    {text && <p className="text-slate-600 text-sm sm:text-base mt-3 max-w-2xl leading-relaxed">{text}</p>}
  </Reveal>
);

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeProblem, setActiveProblem] = useState(PROBLEMS[0]);

  usePageMeta(
    "Productos prácticos para el día a día | solsdiarias360",
    "Productos prácticos que resuelven problemas cotidianos: organización, cocina, limpieza, coche y viajes. Recomendaciones transparentes con enlaces a Amazon España."
  );

  useEffect(() => {
    api.get("/products", { params: { limit: 200 } }).then((r) => setProducts(r.data.products)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => scrollToId(id), 500);
    }
  }, []);

  const featuredList = products.filter((p) => p.featured);
  const favoritos = (featuredList.length ? featuredList : products.filter((p) => p.badge === "favorito")).slice(0, 8);
  const ofertas = products.filter((p) => p.badge === "oferta").slice(0, 8);
  const novedades = products.filter((p) => p.badge === "nuevo").slice(0, 8);
  const problemProducts = products.filter((p) => (p.tags || []).includes(activeProblem.tag)).slice(0, 4);

  return (
    <div className="App">
      <Header />
      <main>
        <Hero />
        <Marquee />

        <section id="categorias" className="py-16 sm:py-20 bg-white" data-testid="categories-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle
              eyebrow="Explora por categoría"
              title={`${["", "Una", "Dos", "Tres", "Cuatro", "Cinco", "Seis", "Siete", "Ocho", "Nueve", "Diez", "Once", "Doce", "Trece", "Catorce", "Quince", "Dieciséis", "Diecisiete", "Dieciocho", "Diecinueve", "Veinte"][categories.length] || categories.length} formas de simplificar tu día`}
              text="Cada categoría reúne productos elegidos por su utilidad real: resolver un problema concreto sin complicaciones."
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-10">
              {categories.map((c, i) => (
                <Reveal key={c.slug} delay={(i % 4) * 0.06}>
                  <Link
                    to={`/categoria/${c.slug}`}
                    data-testid={`category-card-${c.slug}`}
                    className="group block bg-slate-50 border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-[box-shadow,transform] duration-300 h-full"
                  >
                    <div className="h-44 overflow-hidden">
                      <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    </div>
                    <div className="p-4">
                      <h3 className="font-display font-semibold text-slate-900 text-base leading-tight group-hover:text-amber-600 transition-colors">{c.name}</h3>
                      <p className="text-xs text-slate-500 mt-1.5">{c.count} productos</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="favoritos" className="py-16 sm:py-20 bg-slate-50" data-testid="bestsellers-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle
              eyebrow="Los más buscados"
              title="Los favoritos del día a día"
              text="Productos de utilidad cotidiana seleccionados por su capacidad para resolver problemas concretos y por su popularidad entre los compradores de Amazon España."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-10">
              {favoritos.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>

        <section id="soluciones" className="py-16 sm:py-20 bg-white" data-testid="problems-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle
              eyebrow="Dinos qué te pasa"
              title="Soluciones para problemas cotidianos"
              text="Elige la frustración que mejor te describe y te mostramos productos pensados para acabar con ella."
            />
            <div className="flex gap-2 overflow-x-auto scrollbar-hide mt-8 pb-1" data-testid="problem-tabs">
              {PROBLEMS.map((pr) => (
                <button
                  key={pr.id}
                  onClick={() => setActiveProblem(pr)}
                  data-testid={`problem-tab-${pr.id}`}
                  className={`whitespace-nowrap px-4 py-2.5 rounded-full text-sm font-semibold transition-colors border ${
                    activeProblem.id === pr.id
                      ? "bg-slate-950 text-white border-slate-950"
                      : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
                  }`}
                >
                  {pr.label}
                </button>
              ))}
            </div>
            <motion.div
              key={activeProblem.id}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-8"
            >
              <div className="flex items-start gap-3 mb-6 max-w-2xl">
                <MessageCircleQuestion className="text-amber-500 shrink-0 mt-1" size={22} />
                <div>
                  <h3 className="font-display font-bold text-slate-900 text-lg">{activeProblem.title}</h3>
                  <p className="text-slate-600 text-sm mt-1">{activeProblem.text}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {problemProducts.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            </motion.div>
          </div>
        </section>

        <section id="ofertas" className="py-16 sm:py-20 bg-slate-50" data-testid="deals-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle
              eyebrow="Relación calidad-precio"
              title="Productos que merecen la pena"
              text="Seleccionados por su utilidad y su valor práctico. Sin descuentos inventados ni falsas urgencias: el precio aplicable es siempre el que muestre Amazon."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-10">
              {ofertas.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>

        <section id="novedades" className="py-16 sm:py-20 bg-white" data-testid="new-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle
              eyebrow="Recién llegados"
              title="Nuevos descubrimientos"
              text="Las últimas soluciones que hemos incorporado al catálogo."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-10">
              {novedades.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>

        <section id="guias" className="py-16 sm:py-20 bg-slate-50" data-testid="guides-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <SectionTitle
                eyebrow="Contenido editorial"
                title="Guías y consejos prácticos"
                text="Artículos originales para ordenar mejor tu casa, tu cocina, tu coche y tus viajes."
              />
              <Reveal delay={0.1}>
                <Link to="/guias" data-testid="all-guides-link" className="text-sm font-semibold text-amber-600 hover:text-amber-700 flex items-center gap-1.5">
                  Todas las guías <ArrowRight size={15} />
                </Link>
              </Reveal>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
              {GUIDES.slice(0, 3).map((g, i) => (
                <Reveal key={g.slug} delay={i * 0.08}>
                  <Link
                    to={`/guia/${g.slug}`}
                    data-testid={`guide-card-${g.slug}`}
                    className="group block bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-[box-shadow,transform] duration-300 h-full"
                  >
                    <div className="h-44 overflow-hidden">
                      <img src={g.image} alt={g.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-5">
                      <p className="text-[11px] font-mono-s360 text-amber-600 uppercase tracking-widest mb-2">{g.minutes} min de lectura</p>
                      <h3 className="font-display font-bold text-slate-900 text-lg leading-snug group-hover:text-amber-600 transition-colors">{g.title}</h3>
                      <p className="text-slate-600 text-sm mt-2 leading-relaxed line-clamp-3">{g.excerpt}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-slate-950 text-white hero-grain relative overflow-hidden" data-testid="trust-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-8">
            {[
              { icon: ShieldCheck, title: "Transparencia total", text: "Te decimos cuándo un enlace es de afiliado y nunca inventamos precios, valoraciones ni descuentos." },
              { icon: Truck, title: "Compra en Amazon", text: "No vendemos directamente: consultas el producto aquí y completas la compra en Amazon.es con sus garantías." },
              { icon: MessageCircleQuestion, title: "Centrados en problemas", text: "Recomendamos por necesidad real, no por moda: cada producto explica qué problema cotidiano resuelve." },
            ].map((item, i) => (
              <Reveal key={item.title} delay={i * 0.1} className="flex gap-4">
                <item.icon className="text-amber-400 shrink-0" size={26} />
                <div>
                  <h3 className="font-display font-bold text-lg">{item.title}</h3>
                  <p className="text-slate-400 text-sm mt-2 leading-relaxed">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      </main>
      <Footer />
      <CookieBanner />
    </div>
  );
}
